// Hoa hồng CTV — Đại lý (GĐ8, §2.5, §5.2):
//  · "Bảng hoa hồng": thư viện bảng theo số đơn (tạo, sửa = phiên bản tháng sau, lưu trữ).
//  · "CTV của tôi": bảng đang áp / từ tháng sau, số đơn & hoa hồng tạm tính theo tháng, tiến độ
//    lên bậc, gán bảng (xem trước trần — D25), xem từng đơn đã ghi hoa hồng.
// Đại lý là bên trả: hoa hồng trừ vào chênh lệch của chính Đại lý trên các đơn được tính.

import { useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query'
import { AlertTriangle, CalendarClock, Coins, Layers, ListChecks, UserX, Users } from 'lucide-react'
import { toast } from 'sonner'
import { get } from '@/lib/api'
import { money } from '@/lib/format'
import { cn } from '@/lib/utils'
import { useUnitCan } from '@/lib/unitAccess'
import { Button } from '@/components/ui/button'
import { PageHeader } from '@/components/ui/page-header'
import { LoadError } from '@/components/ui/data-notice'
import { EmptyState } from '@/components/ui/empty-state'
import { KpiTile } from '@/components/ui/kpi-tile'
import { Sheet } from '@/components/ui/sheet'
import { ViewSwitcher, type ViewOption } from '@/components/ui/view-switcher'
import DataTable, { IndexBadge, type DataTableColumn } from '@/components/ui/data-table'
import { SchemeFormDialog, type SchemeSaved } from '@/components/commissions/SchemeFormDialog'
import { SchemeDetailDialog } from '@/components/commissions/SchemeDetailDialog'
import { AssignSchemeDialog } from '@/components/commissions/AssignSchemeDialog'
import { CommissionLinesTable, MonthStepper, ProgressBar } from '@/components/commissions/parts'
import {
  MODE_SHORT,
  MONTH_STATUS_LABEL,
  ctvsBySchemeAt,
  currentMonthVN,
  monthLabel,
  scheduledVersion,
  shiftMonth,
  tierFraction,
  tiersSummary,
  versionAt,
  vnd,
  type CommissionLine,
  type CtvRow,
  type Scheme,
} from '@/components/commissions/types'

type View = 'schemes' | 'ctvs'

const UNIT_STATUS: Record<string, string> = { locked: 'Đang khóa', suspended: 'Tạm ngưng', grace: 'Quá hạn', deleted: 'Đã xóa' }

interface CtvsResponse {
  month: string
  ctvs: CtvRow[]
}

export default function AgencyCommissionsPage() {
  const [params, setParams] = useSearchParams()
  const view: View = params.get('tab') === 'ctvs' ? 'ctvs' : 'schemes'
  const nowMonth = currentMonthVN()
  const [schemeForm, setSchemeForm] = useState<SchemeForm>(null)
  // Nhân viên (docs/KE_HOACH_THANH_VIEN_DON_VI.md): tạo / sửa / gán bảng cần "Bảng giá & hoa hồng cấp dưới"
  const canPricing = useUnitCan().can('network.pricing')

  const schemesQ = useQuery({
    queryKey: ['commissions', 'schemes'],
    queryFn: () => get<{ schemes: Scheme[] }>('/commissions/schemes?includeArchived=true'),
  })
  // CTV tháng này — để hiện tên CTV trong chi tiết bảng (dùng chung cache với tab "CTV của tôi").
  const ctvsNowQ = useQuery({
    queryKey: ['commissions', 'ctvs', nowMonth],
    queryFn: () => get<CtvsResponse>(`/commissions/ctvs?month=${nowMonth}`),
  })

  // Đại lý từng là CTV (đã nâng cấp) — còn lịch sử hoa hồng của mình (403 khi chưa từng là CTV)
  const myCtvMonthsQ = useQuery({
    queryKey: ['commissions', 'me-months'],
    queryFn: () => get<{ months: unknown[] }>('/commissions/me/months'),
    retry: false,
  })
  const wasCtv = (myCtvMonthsQ.data?.months.length ?? 0) > 0

  const schemes = schemesQ.data?.schemes ?? []
  const active = schemes.filter((s) => !s.archivedAt)

  const options: ViewOption[] = [
    {
      value: 'schemes',
      label: 'Bảng chiết khấu',
      icon: Layers,
      tone: 'violet',
      count: schemesQ.isSuccess ? active.length : undefined,
    },
    {
      value: 'ctvs',
      label: 'CTV của tôi',
      icon: Users,
      tone: 'blue',
      count: ctvsNowQ.isSuccess ? ctvsNowQ.data.ctvs.length : undefined,
    },
  ]

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <PageHeader
        title="Chiết khấu"
        actions={
          view === 'schemes' || wasCtv ? (
            <>
              {wasCtv && (
                <Button variant="outline" asChild>
                  <Link to="/commissions/me">Chiết khấu thời làm CTV</Link>
                </Button>
              )}
              {view === 'schemes' && canPricing && <Button onClick={() => setSchemeForm({ scheme: null })}>Tạo bảng chiết khấu</Button>}
            </>
          ) : undefined
        }
      />
      <ViewSwitcher
        value={view}
        onChange={(v) => setParams(v === 'schemes' ? {} : { tab: v }, { replace: true })}
        options={options}
      />
      {view === 'schemes' ? (
        <SchemesSection q={schemesQ} month={nowMonth} ctvs={ctvsNowQ.data?.ctvs ?? []} form={schemeForm} setForm={setSchemeForm} />
      ) : (
        <CtvsSection schemesQ={schemesQ} nowMonth={nowMonth} nowCtvs={ctvsNowQ.data?.ctvs ?? []} />
      )}
    </div>
  )
}

// ─── Bảng hoa hồng ───────────────────────────────────────────────

type SchemeForm = { scheme: Scheme | null } | null

function SchemesSection({
  q,
  month,
  ctvs,
  form,
  setForm,
}: {
  q: UseQueryResult<{ schemes: Scheme[] }>
  month: string
  ctvs: CtvRow[]
  /** Hộp tạo/sửa bảng — nút "Tạo bảng hoa hồng" nằm ở đầu trang */
  form: SchemeForm
  setForm: (f: SchemeForm) => void
}) {
  const qc = useQueryClient()
  const [showArchived, setShowArchived] = useState(false)
  const [detailId, setDetailId] = useState<string | null>(null)
  const schemes = q.data?.schemes ?? []
  const rows = schemes.filter((s) => showArchived || !s.archivedAt)
  const archivedCount = schemes.length - schemes.filter((s) => !s.archivedAt).length

  const usingNow = useMemo(() => ctvsBySchemeAt(schemes, month), [schemes, month])
  const usingNext = useMemo(() => ctvsBySchemeAt(schemes, shiftMonth(month, 1)), [schemes, month])
  const names = useMemo(() => new Map(ctvs.map((c) => [c.shopId, c.name])), [ctvs])
  const ctvName = (id: string) => names.get(id) ?? 'CTV'
  const detail = schemes.find((s) => s.id === detailId) ?? null

  function onSaved(r: SchemeSaved) {
    if (r.kind === 'created') toast.success(`Đã tạo bảng ${r.code}`)
    else if (r.effectiveFromMonth) toast.success(`Đã lưu ${r.code} v${r.version} — áp dụng từ tháng ${monthLabel(r.effectiveFromMonth)}`)
    else toast.success(`Đã đổi tên bảng ${r.code}`)
    setForm(null)
    qc.invalidateQueries({ queryKey: ['commissions'] })
  }

  return (
    <section className="space-y-4">
      {q.isError ? (
        <LoadError onRetry={() => q.refetch()}>Không tải được bảng chiết khấu.</LoadError>
      ) : q.isLoading ? (
        <p className="p-8 text-center text-sm text-muted">Đang tải…</p>
      ) : rows.length === 0 ? (
        <EmptyState
          className="rounded-xl bg-raise shadow-card"
          title={
            archivedCount > 0
              ? 'Không còn bảng chiết khấu nào đang dùng được.'
              : 'Chưa có bảng chiết khấu nào.'
          }
        />
      ) : (
        <ul className="divide-y divide-rule overflow-hidden rounded-xl bg-raise shadow-card">
          {rows.map((s, i) => {
            const cur = versionAt(s, month)
            const next = scheduledVersion(s, month)
            const nowIds = usingNow.get(s.id) ?? []
            const nowCount = s.inUse ?? nowIds.length
            const joining = (usingNext.get(s.id) ?? []).filter((id) => !nowIds.includes(id)).length
            return (
              <li key={s.id}>
                <button
                  type="button"
                  onClick={() => setDetailId(s.id)}
                  className={cn('motion-state flex w-full flex-wrap items-center gap-x-4 gap-y-1 p-4 text-left hover:bg-paperSoft', s.archivedAt && 'opacity-60')}
                >
                  <div className="min-w-0 flex-1 space-y-0.5">
                    <p className="flex items-center gap-2 font-medium text-ink">
                      <IndexBadge n={i + 1} />
                      <span className="min-w-0 flex-1 truncate">
                        {s.name}
                        {s.archivedAt && <span className="ml-2 rounded-full bg-paperSoft px-2 py-0.5 text-xs font-normal text-inkSoft">Đã lưu trữ</span>}
                      </span>
                    </p>
                    <p className="text-xs text-inkSoft">
                      <span className="font-mono">{s.code}</span> · {MODE_SHORT[cur?.mode ?? s.mode]} · v{cur?.version ?? s.version}
                    </p>
                    <p className="text-xs tabular-nums text-ink">
                      {tiersSummary(cur?.tiers ?? s.tiers)}
                    </p>
                    {next && (
                      <p className="flex items-center gap-1 text-xs font-medium text-warn">
                        <CalendarClock className="size-3.5" aria-hidden /> Từ tháng {monthLabel(next.effectiveFromMonth)}: v{next.version} — {tiersSummary(next.tiers)}
                      </p>
                    )}
                  </div>
                  <span className="text-right">
                    <span className={cn('block text-sm tabular-nums', nowCount > 0 ? 'text-ink' : 'text-muted')}>{nowCount} CTV</span>
                    {joining > 0 && <span className="block text-xs text-inkSoft">+{joining} từ tháng sau</span>}
                  </span>
                </button>
              </li>
            )
          })}
        </ul>
      )}

      {archivedCount > 0 && (
        <label className="inline-flex cursor-pointer items-center gap-2 text-sm text-inkSoft">
          <input type="checkbox" className="size-4 accent-accent" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} />
          Hiện {archivedCount} bảng đã lưu trữ
        </label>
      )}

      {form && (
        <SchemeFormDialog
          scheme={form.scheme}
          inUse={form.scheme ? new Set([...(usingNow.get(form.scheme.id) ?? []), ...(usingNext.get(form.scheme.id) ?? [])]).size : 0}
          month={month}
          onClose={() => setForm(null)}
          onSaved={onSaved}
        />
      )}
      {detail && (
        <SchemeDetailDialog
          scheme={detail}
          month={month}
          usingNow={usingNow.get(detail.id) ?? []}
          usingNext={(usingNext.get(detail.id) ?? []).filter((id) => !(usingNow.get(detail.id) ?? []).includes(id))}
          ctvName={ctvName}
          onClose={() => setDetailId(null)}
          onEdit={() => {
            setDetailId(null)
            setForm({ scheme: detail })
          }}
        />
      )}
    </section>
  )
}

// ─── CTV của tôi ─────────────────────────────────────────────────

function CtvsSection({
  schemesQ,
  nowMonth,
  nowCtvs,
}: {
  schemesQ: UseQueryResult<{ schemes: Scheme[] }>
  nowMonth: string
  /** CTV ở tháng hiện tại — bảng đang áp / đã hẹn, dùng khi gán bảng dù đang xem tháng cũ. */
  nowCtvs: CtvRow[]
}) {
  const qc = useQueryClient()
  const [month, setMonth] = useState(nowMonth)
  const [assignFor, setAssignFor] = useState<CtvRow | null>(null)
  const canAssign = useUnitCan().can('network.pricing')
  const [linesFor, setLinesFor] = useState<CtvRow | null>(null)

  const q = useQuery({
    queryKey: ['commissions', 'ctvs', month],
    queryFn: () => get<CtvsResponse>(`/commissions/ctvs?month=${month}`),
  })

  const schemes = schemesQ.data?.schemes ?? []
  const ctvs = q.data?.ctvs ?? []
  const totalAccrued = ctvs.reduce((s, c) => s + c.accrued, 0)
  const totalOrders = ctvs.reduce((s, c) => s + c.totalOrders, 0)
  const unassigned = ctvs.filter((c) => !c.scheme).length
  const closed = ctvs.some((c) => c.monthStatus === 'closed') && ctvs.every((c) => c.monthStatus !== 'provisional')
  const isNow = month === nowMonth

  const assignedCtvs = useMemo(() => new Set(schemes.flatMap((s) => s.assignments.map((a) => a.ctvShopId))), [schemes])

  const tiersOf = (c: CtvRow) => {
    const s = c.scheme ? schemes.find((x) => x.id === c.scheme!.id) : null
    return s ? (versionAt(s, month)?.tiers ?? null) : null
  }

  // Mức cao nhất của bảng CTV đang áp (tháng hiện tại) và bảng đã hẹn từ tháng sau — so với trần.
  const topRate = (schemeId: string | undefined, m: string): number | null => {
    const s = schemeId ? schemes.find((x) => x.id === schemeId) : null
    const tiers = s ? (versionAt(s, m)?.tiers ?? null) : null
    return tiers?.length ? Math.max(...tiers.map((t) => t.amountPerOrder)) : null
  }

  const columns: DataTableColumn<CtvRow>[] = [
    {
      key: 'ctv',
      header: 'CTV',
      priority: 'primary',
      cell: (c) => (
        <span className="block min-w-0">
          <span className="block truncate font-medium text-ink">{c.name}</span>
          {c.left ? (
            <span className="block text-xs text-muted">Đã rời nhánh</span>
          ) : (
            c.status !== 'active' && <span className="block text-xs text-warn">{UNIT_STATUS[c.status] ?? c.status}</span>
          )}
        </span>
      ),
    },
    {
      key: 'scheme',
      header: 'Bảng đang áp',
      priority: 'secondary',
      cell: (c) => (
        <span className="block min-w-0">
          {c.scheme ? (
            <span className="block truncate text-ink">
              {c.scheme.name} <span className="font-mono text-xs text-muted">{c.scheme.code}</span>
            </span>
          ) : (
            <span className="block text-warn">Chưa gán bảng</span>
          )}
          {c.nextScheme && (
            <span className="block truncate text-xs text-inkSoft">
              Từ tháng {monthLabel(c.nextScheme.effectiveFromMonth)}: {c.nextScheme.name}
            </span>
          )}
        </span>
      ),
    },
    {
      key: 'cap',
      header: 'Trần chiết khấu',
      cell: (c) => {
        // Trần theo giá hiện hành — so với bảng đang áp tháng này và bảng đã hẹn từ tháng sau.
        const cap = c.cap ?? nowRowOf(c).cap ?? null
        if (!cap) return <span className="text-xs text-muted">Chưa tính được</span>
        const now = nowRowOf(c)
        const curTop = topRate(now.scheme?.id, nowMonth)
        const nextTop = now.nextScheme ? topRate(now.nextScheme.id, now.nextScheme.effectiveFromMonth) : null
        const over = curTop != null && curTop > cap.minMargin ? curTop : null
        const overNext = nextTop != null && nextTop > cap.minMargin ? nextTop : null
        return (
          <span className="block min-w-[9rem]">
            <span className="block whitespace-nowrap tabular-nums text-ink">{vnd(cap.minMargin)}/đơn</span>
            <span className="block text-xs text-muted">ô {cap.at}</span>
            {over != null && (
              <span className="mt-0.5 flex items-start gap-1 text-xs font-medium text-danger">
                <AlertTriangle className="mt-px size-3.5 shrink-0" aria-hidden />
                Bảng đang áp tới {vnd(over)}/đơn — vượt trần
              </span>
            )}
            {overNext != null && (
              <span className="mt-0.5 flex items-start gap-1 text-xs font-medium text-warn">
                <AlertTriangle className="mt-px size-3.5 shrink-0" aria-hidden />
                Bảng từ tháng {monthLabel(now.nextScheme!.effectiveFromMonth)} tới {vnd(overNext)}/đơn — vượt trần
              </span>
            )}
          </span>
        )
      },
    },
    {
      key: 'orders',
      header: 'Số đơn tháng',
      align: 'right',
      cell: (c) => <span className="tabular-nums text-ink">{c.totalOrders.toLocaleString('vi-VN')}</span>,
    },
    {
      key: 'accrued',
      header: 'Chiết khấu',
      mobileLabel: 'Chiết khấu',
      align: 'right',
      cell: (c) => (
        <span className="block">
          <span className="block whitespace-nowrap font-mono font-semibold tabular-nums text-ink">{money(c.accrued)}</span>
          <span className="block text-xs text-muted">{c.monthStatus ? MONTH_STATUS_LABEL[c.monthStatus] : 'Chưa có đơn'}</span>
        </span>
      ),
    },
    {
      key: 'progress',
      header: 'Tiến độ',
      cell: (c) => {
        if (!c.progress) return <span className="text-xs text-muted">—</span>
        const tiers = tiersOf(c)
        return (
          <span className="block min-w-[10rem] space-y-1">
            <span className="block text-xs text-ink">
              Hôm nay {(c.todayOrders ?? 0).toLocaleString('vi-VN')} đơn · {vnd(c.progress.currentRate)}/đơn
              {c.progress.nextRate != null && c.progress.ordersToNext != null ? (
                <span className="text-inkSoft">
                  {' '}
                  · còn {c.progress.ordersToNext.toLocaleString('vi-VN')} đơn lên {vnd(c.progress.nextRate)}
                </span>
              ) : (
                <span className="text-inkSoft"> · bậc cao nhất</span>
              )}
            </span>
            {tiers && <ProgressBar value={tierFraction(tiers, c.todayOrders ?? 0)} label={`Tiến độ lên bậc của ${c.name}`} className="h-1.5" />}
          </span>
        )
      },
    },
    {
      key: 'actions',
      header: '',
      priority: 'action',
      cell: (c) => (
        <span className="flex flex-wrap items-center justify-end gap-2">
          <Button size="sm" variant="outline" onClick={() => setLinesFor(c)}>
            Xem đơn
          </Button>
          {!c.left && canAssign && (
            <Button size="sm" variant="outline" onClick={() => setAssignFor(c)}>
              Gán bảng
            </Button>
          )}
        </span>
      ),
    },
  ]

  // Bảng đang áp / đã hẹn của CTV theo tháng hiện tại (cho hộp gán bảng).
  const nowRowOf = (c: CtvRow): CtvRow => (isNow ? c : (nowCtvs.find((x) => x.shopId === c.shopId) ?? c))

  return (
    <section className="space-y-4">
      <div className="flex flex-wrap items-center justify-end gap-3">
        <MonthStepper value={month} onChange={setMonth} max={nowMonth} />
      </div>

      <div className="grid grid-cols-2 gap-3 xl:grid-cols-3">
        <KpiTile
          tone="orange"
          icon={Coins}
          label={`Chiết khấu tháng ${monthLabel(month)}`}
          value={money(totalAccrued)}
          hint={closed ? 'Đã chốt' : 'Tạm tính'}
          loading={q.isLoading}
          error={q.isError}
        />
        <KpiTile
          tone="blue"
          icon={ListChecks}
          label="Đơn tính chiết khấu"
          value={totalOrders.toLocaleString('vi-VN')}
          loading={q.isLoading}
          error={q.isError}
        />
        <KpiTile
          tone="gray"
          icon={UserX}
          label="CTV chưa gán bảng"
          value={`${unassigned}/${ctvs.length}`}
          loading={q.isLoading}
          error={q.isError}
          className="col-span-2 xl:col-span-1"
        />
      </div>

      <div className="overflow-hidden rounded-xl bg-raise shadow-card">
        {q.isError ? (
          <LoadError onRetry={() => q.refetch()}>Không tải được danh sách CTV.</LoadError>
        ) : (
          <DataTable
            columns={columns}
            data={ctvs}
            rowKey={(c) => c.shopId}
            loading={q.isLoading}
            emptyLabel="Bạn chưa có CTV trực thuộc."
          />
        )}
      </div>

      {assignFor && (
        <AssignSchemeDialog
          ctv={nowRowOf(assignFor)}
          schemes={schemes.filter((s) => !s.archivedAt)}
          month={nowMonth}
          hasAssignment={!!(nowRowOf(assignFor).scheme || nowRowOf(assignFor).nextScheme) || assignedCtvs.has(assignFor.shopId)}
          onClose={() => setAssignFor(null)}
          onAssigned={(r) => {
            toast.success(`Đã gán ${r.code} cho ${assignFor.name} — áp dụng từ tháng ${monthLabel(r.effectiveFromMonth)}`)
            setAssignFor(null)
            qc.invalidateQueries({ queryKey: ['commissions'] })
          }}
        />
      )}

      <Sheet
        open={!!linesFor}
        onOpenChange={(o) => !o && setLinesFor(null)}
        title={linesFor ? `Chiết khấu của ${linesFor.name}` : 'Chiết khấu'}
        description={linesFor ? `Tháng ${monthLabel(month)} · ${linesFor.totalOrders.toLocaleString('vi-VN')} đơn · ${money(linesFor.accrued)}` : undefined}
      >
        {linesFor && <CtvLines ctv={linesFor} month={month} />}
      </Sheet>
    </section>
  )
}

function CtvLines({ ctv, month }: { ctv: CtvRow; month: string }) {
  const q = useQuery({
    queryKey: ['commissions', 'ctv-lines', ctv.shopId, month],
    queryFn: () => get<{ lines: CommissionLine[] }>(`/commissions/ctvs/${encodeURIComponent(ctv.shopId)}/lines?month=${month}`),
  })
  if (q.isError) return <LoadError onRetry={() => q.refetch()}>Không tải được các đơn tính chiết khấu.</LoadError>
  const lines = q.data?.lines ?? []
  return (
    <div className="space-y-3">
      {lines.length >= 200 && <p className="text-xs text-muted">Hiện 200 dòng gần nhất.</p>}
      <div className="overflow-hidden rounded-lg border border-rule">
        <CommissionLinesTable lines={lines} loading={q.isLoading} ctvShopId={ctv.shopId} />
      </div>
    </div>
  )
}
